import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GridReadyEvent, ICellRendererParams, ViewportChangedEvent } from '@ag-grid-community/core';
import { fromEvent, Subscription, forkJoin, of, catchError } from 'rxjs';
import { FieldExpression, LuceneExpression, NodeExpression, toString as luceneToString } from 'lucene';
import { MatDialog } from '@angular/material/dialog';
import { AgGridModule } from '@ag-grid-community/angular';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor, NgClass } from '@angular/common';
import moment from 'moment';

import { padSupplierOrderReference } from '../../../shared/utils/padding';
import { WipOrder } from '../../models/wip-order';
import { LocationService } from '../../../shared/services/location.service';
import { UserService } from '../../../shared/services/user.service';
import { FieldOfOrder, Order, orderCardFields } from '../../models/order';
import { DialogResponse, ExceptionReportToDisplay } from '../../models/exception-report';
import { FavoriteOrders } from '../../models/favorite-orders';
import { OrderService } from '../../services/order.service';
import { DialogPinComponent } from '../../../shared/datacenter/cell-renderers/dialog-pin/dialog-pin.component';
import { UNDERSCORE, OrderByDirection, OrderBy } from '../../../shared/utils/constant';
import { TranslationService } from '../../../shared/services/translation.service';
import { Batch, FieldOfBatch, OrderBatch } from '../../models/batch';
import { User } from '../../../shared/models/user';
import { CommonGridOptions, clientSideAgGridModules } from '../../../shared/datacenter/grid-options';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import {
	CommonColDef,
	DateColDef,
	IconRedirectColDef,
	LinkColDef,
	NumericColDef,
	NumericPercentageColDef
} from '../../../shared/datacenter/column-defs';
import { PaginatedResult } from '../../../shared/models/paginated-result';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import {
	Document,
	DOCUMENTS_SUB_ENTITY,
	FieldOfDocument,
	GlobalDocument
} from '../../../commercial-documents/models/document';
import { ECallOffService } from '../../../ecall-off/services/ecall-off.service';
import {
	EcallOffCardItemResponse,
	EcallOffCardResponse,
	Ecalls
} from '../../../ecall-off/models/ecall-off-model';
import { CheckboxHeaderComponent } from '../../../shared/datacenter/header-components/checkbox-header.component';
import { DatacenterFilterType } from '../../../shared/models/datacenter-filters';
import { DatacenterDefaultConfiguration } from '../../../shared/datacenter/datacenter-default-configuration';
import { UserDatacenterView } from '../../../shared/models/datacenter-aggrid-view';
import { ModelInfoPipe } from '../../../shared/pipes/model-info.pipe';
import { CharacPipe } from '../../../shared/pipes/characteristic.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { Characteristic } from '../../models/characteristic';
import { BatchesService } from '../../services/batches.service';
import { ExceptionReportService } from '../../services/exception-report.service';

import { OrderStatusIconHeaderComponent } from './order-status-icon-header.component';
import { ElinkComponent } from './elink.component';
import { ElinkData } from './elink-data';
import {
	GroupedOrderBatches,
	createOrderBatchesFilter,
	prepareOrderCardBatches
} from './order-card-batch.utils';
import { OrderCardBatchGridBuilderService } from './order-card-batch-grid.builder.service';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';
const LIMIT = 500;
const DELIVERED_BATCH_DISPLAY_LIMIT = 10;

const resizeObservable = fromEvent(globalThis, 'resize');

interface OrderStatusData {
	title: string;
	production?: number;
	stock: number;
	transit: number;
	consignment: number;
	delivered: number;
	type: string;

}

const linkToDocumentCellRendererParams = {
	routerLink(params: ICellRendererParams<Document, unknown>) {
		if (!params.data) {
			return undefined;
		}
		const document = params.data;
		let documentType;
		switch (document.type) {
		case 'IN':
			documentType = 'invoice-card';
			break;
		case 'CE':
			documentType = 'certificate-card';
			break;
		case 'DA':
			documentType = 'dispatch-advice-card';
			break;
		case 'OA':
			documentType = 'order-acknowledgment-card';
			break;
		case 'XCARB':
			documentType = 'xcarb-document-card';
			break;
		default:
			throw new Error('unknown document type');
		}

		return {
			commands: [
				'/commercial-documents',
				'cards',
				documentType,
				document.id
			],
		};
	}
};

enum OrderStatus {
	DELIVERED = 'status_delivered',
	FULL = 'status_full',
	RTS = 'status_rts',
	RTS_DELIVERED = 'status_rts_delivered',
	WIP = 'status_wip',
	WIP_DELIVERED = 'status_wip_delivered',
	WIP_RTS = 'status_wip_rts'
}

@Component({
	selector: 'copo-order-card',
	templateUrl: './order-card.component.html',
	styleUrl: './order-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		BackButtonComponent,
		TranslateModule,
		MatIconModule,
		MatButtonModule,
		MatExpansionModule,
		MatDividerModule,
		AgGridModule,
		NgFor,
		AfterViewInitDirective,
		NgClass,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		CharacPipe,
		ModelInfoPipe
	]
})
export class OrderCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	isLoading = false;
	isOffline = false;
	isDisabled = false;
	supplierReference: string;
	supplierReferenceItem: string;
	order?: Partial<Order>;
	orderBatches?: OrderBatch[];
	documentResult?: PaginatedResult<Document>;
	iconName?: string | null;

	invoiceDisplayed = false;
	certificateDisplayed = false;
	isXcarbDisplayed = false;
	dispatchAdviceDisplayed = false;
	orderAcknowledgmentDisplayed = false;
	rowDataDeliveredBatches?: OrderBatch[];
	gridBatchDeliveredOptions?: CommonGridOptions<OrderBatch>;
	sumGrossDeliveredBatches = 0;
	sumNetDeliveredBatches = 0;
	batchesSubscription?: Subscription;
	rowDataReadyToShipBatches?: Batch[];
	gridBatchReadyToShipOptions?: CommonGridOptions<Batch>;
	sumGrossReadyToShipBatches = 0;
	sumNetReadyToShipBatches = 0;
	rowDataCalledBatches?: Batch[];
	gridBatchCalledOptions?: CommonGridOptions<Batch>;
	sumGrossCalledBatches = 0;
	sumNetCalledBatches = 0;
	rowDataLandTransitBatches?: OrderBatch[];
	gridBatchLandTransitOptions?: CommonGridOptions<OrderBatch>;
	sumGrossLandTransitBatches = 0;
	sumNetLandTransitBatches = 0;

	rowDataAtReshipperBatches?: OrderBatch[];
	gridBatchAtReshipperOptions?: CommonGridOptions<OrderBatch>;
	sumGrossAtReshipperBatches = 0;
	sumNetAtReshipperBatches = 0;

	rowDataOnVesselBatches?: OrderBatch[];
	gridBatchOnVesselOptions?: CommonGridOptions<OrderBatch>;
	sumGrossOnVesselBatches = 0;
	sumNetOnVesselBatches = 0;

	rowDataOverseasTransitBatches?: OrderBatch[];
	gridBatchOverseasTransitOptions?: CommonGridOptions<OrderBatch>;
	sumGrossOverseasTransitBatches = 0;
	sumNetOverseasTransitBatches = 0;

	rowDataTransitToCustomerBatches?: OrderBatch[];
	gridBatchTransitToCustomerOptions?: CommonGridOptions<OrderBatch>;
	sumGrossTransitToCustomerBatches = 0;
	sumNetTransitToCustomerBatches = 0;

	rowDataConsignmentStockBatches?: OrderBatch[];
	gridBatchConsignmentStockOptions?: CommonGridOptions<OrderBatch>;
	gridEcallCalledBatches?: CommonGridOptions;
	gridEcalledVolumes?: CommonGridOptions;
	sumGrossConsignmentStockBatches = 0;
	sumNetConsignmentStockBatches = 0;

	rowDataBlockedBatches?: OrderBatch[];
	gridBatchBlockedOptions?: CommonGridOptions<OrderBatch>;
	sumGrossBlockedBatches = 0;
	sumNetBlockedBatches = 0;

	sumGrossAvailableBatches = 0;
	sumNetAvailableBatches = 0;
	callableBatchesCount = 0;
	callableGrossWeight = 0;
	calledGrossWeight = 0;

	wipOrders?: WipOrder[];
	exceptionReportToDisplay: ExceptionReportToDisplay[] = [];
	isEdit = false;
	gridInProductionOptions?: CommonGridOptions<WipOrder>;
	sumInProduction?: number;

	gridOrderStatusOptions?: CommonGridOptions<OrderStatusData>;
	orderStatusFrameworkComponents = { agColumnHeader: OrderStatusIconHeaderComponent };

	orderStatusData?: OrderStatusData[];
	totalOrdered?: number;
	totalInProduction?: number;

	resizeSub?: Subscription;
	elinkSub?: Subscription;
	ecallsByBatchSubscription?: Subscription;

	globalDocuments: GlobalDocument[] = [];
	gridCommercialDocumentsOptions?: CommonGridOptions<GlobalDocument>;

	overlayNoRowsTemplate?: string;
	overlayNoDocumentsTemplate?: string;

	millCode?: string;
	millEmail?: string;
	title?: string;
	limit = 100;
	index = 0;
	documentsFields: readonly FieldOfDocument[] = [
		'id',
		'subtype',
		'date',
		'name',
		'type',
		'proforma'
	];

	ecallData?: EcallOffCardResponse[];
	ecallDataVolume?: Ecalls[];
	isCommercialDocumentFragment = false;
	hasCommercialDocumentError = false;
	hasOrderDetailsError = false;
	hasExceptionReportService = false;
	orderCharacteristics?: Characteristic[];
	dateFormat: DatePipe;
	ecallSub?: Subscription;
	readonly agGridModules = clientSideAgGridModules;
	private readonly id: string | null;
	private saveFavoriteOrdersSubscription?: Subscription;
	private deleteFavoriteOrdersSubscription?: Subscription;
	private dialogSubscription?: Subscription;
	private orderDataSubscription?: Subscription;
	private readonly routeParamsSubscription?: Subscription;
	private elinkData?: ElinkData;
	private readonly previousState: Record<string, unknown> | undefined;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly userService: UserService,
		readonly locationService: LocationService,
		private readonly orderService: OrderService,
		private readonly translationService: TranslationService,
		private readonly commercialDocumentService: DocumentService,

		private readonly dialog: MatDialog,
		private readonly exceptionReportService: ExceptionReportService,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly ecallOffService: ECallOffService,
		private readonly batchservice: BatchesService,
		private readonly batchGridBuilder: OrderCardBatchGridBuilderService
	) {
		this.dateFormat = new DatePipe();
		this.supplierReference = <string> this.route.snapshot.params['supplierReference'];
		this.supplierReferenceItem = <string> this.route.snapshot.params['supplierItem'];

		this.isOffline = !navigator.onLine;
		this.isLoading = true;
		this.user = this.userService.user;

		this.isCommercialDocumentFragment = route.snapshot.fragment === 'documents';
		this.id = route.snapshot.fragment;

		this.overlayNoRowsTemplate = this.translationService.getTR('order-card.no-rows-to-show');
		this.overlayNoDocumentsTemplate = this.translationService.getTR('order.no-documents');
		this.invoiceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Invoice', 'True');
		this.certificateDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'Certificate', 'True');
		this.dispatchAdviceDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'DispatchAdvice', 'True');
		this.orderAcknowledgmentDisplayed =
			this.user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
		this.isXcarbDisplayed = this.user.hasServiceParameterEqualsTo('PDOCS', 'XCarb', 'True');
		this.hasExceptionReportService = this.user.hasServiceParameterEqualsTo('PORDT', 'ExceptionReport', 'True');
		this.routeParamsSubscription = this.route.params.subscribe(params => {
			this.supplierReference = <string>params['supplierReference'];
			this.supplierReferenceItem = <string>params['supplierItem'];
			this.loadOrderCardData();
		});
	}

	loadOrderCardData() {
		this.exceptionReportToDisplay = [];
		this.globalDocuments = [];
		this.hasCommercialDocumentError = false;
		this.hasOrderDetailsError = false;
		this.isLoading = true;

		this.orderDataSubscription = this.getOrderData().subscribe({
			next: ([order, wipOrder, documentResult, favoriteOrders, orderCharacteristicsResult]) => {
				if (Object.keys(order).length === 0) {
					this.isLoading = false;
					this.hasOrderDetailsError = true;
					this.redirectToSACard();
					return;
				}

				this.isLoading = false;
				this.order = this.assignFavoriteOrder(order, favoriteOrders);
				this.millCode = this.order.mill;
				this.wipOrders = wipOrder.results;
				this.documentResult = documentResult;
				if (this.user.hasService('PCALL')) {
					this.getCallableGrossQuantity();
				}
				// Consolidated loading for non-delivered batch groups.
				this.loadAndDistributeBatches();
				this.constructDeliveredGrid();
				this.constructDeliveredGridTotalQty();

				// Non-batch grid construction (dependent on prior data loading)
				this.constructInProductionGrid();
				this.constructOrderStatusGrid();
				this.constructElinkData();
				this.iconName = this.getStatusIcon();
				this.constructCommercialDocuments();
				this.constructExceptionReport(order);
				if (this.user.hasService('PCALL')) {
					this.getEcallsByBatch();
				}
				this.orderCharacteristics = orderCharacteristicsResult.results;
			},
			error: (err: unknown) => {
				console.error('error loading', err);
				this.isLoading = false;
				this.hasOrderDetailsError = true;
				this.redirectToSACard();
			}
		});
	}

	redirectToSACard() {
		if (this.user.hasServiceParameterEqualsTo('PORDT', 'SBOrder', 'True')) {
			this.redirectToScheduleBusinessOrderCard().then(() => {
				// it redirects to schedule business order card
			}).catch((scheduleError: unknown) => {
				console.error('schedule error', scheduleError);
			});
		}
	}

	constructExceptionReport(order: Partial<Order>) {
		const formattedWeek = this.exceptionReportService.getYearWeek();
		if (order.comments) {
			for (const commentCustomer of order.comments) {
				const exceptionReportToDisplay: ExceptionReportToDisplay = {
					yearWeek: commentCustomer.yearWeek,
					commentFromCustomer: commentCustomer.comment,
					docNo: `${ order.supplierOrderNumber }/${ order.supplierOrderItemNumber }`
				};
				if (commentCustomer.yearWeek === formattedWeek) {
					exceptionReportToDisplay.commentExistForCurrentWeek = true;
				}
				this.exceptionReportToDisplay.push(exceptionReportToDisplay);
			}
		}
		if (order.answers) {
			for (const commentCuse of order.answers) {
				const alreadyExist = this.exceptionReportToDisplay.find(ex => ex.yearWeek === commentCuse.yearWeek);

				if (alreadyExist) {
					alreadyExist.commentFromCuse = commentCuse.comment;
				} else {
					const exceptionReportToDisplay: ExceptionReportToDisplay = {
						yearWeek: commentCuse.yearWeek,
						commentFromCuse: commentCuse.comment,
						docNo: `${ order.supplierOrderNumber }/${ order.supplierOrderItemNumber }`
					};
					this.exceptionReportToDisplay.push(exceptionReportToDisplay);
				}
			}
		}
		const currentWeekComment = this.exceptionReportToDisplay.find(exc => exc.yearWeek === formattedWeek);
		if (currentWeekComment) {
			if (currentWeekComment.commentExistForCurrentWeek) {
				this.isEdit = true;
			}
			order.exceptionReport = currentWeekComment;
		}
	}

	async redirectToScheduleBusinessOrderCard() {
		await this.router.navigate(
			[`/order-tracking/schedule-business-order/card/${ this.supplierReference }`],
			{ replaceUrl: true }
		);
	}

	autoScrollToElement(documentEl: HTMLElement) {
		if (this.id === 'documents') {
			documentEl.scrollIntoView();
		}
	}

	sumGross(batches: { qtyBatchGross: number }[]) {
		return batches.reduce((previous, current) => previous + current.qtyBatchGross, 0);
	}

	sumNet(batches: { qtyBatchNet: number }[]) {
		return batches.reduce((previous, current) => previous + current.qtyBatchNet, 0);
	}

	sumProduction(wip: WipOrder[]) {
		return wip.reduce((previous, current) => previous + current.qtyWorkInProgress, 0);
	}

	checkCddWeekData() {
		return this.orderService.checkCddWeekData(this.order?.cddWeek);
	}

	onGridReady(params: GridReadyEvent) {
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}

	getPrevisionItem() {
		if (!this.order) {
			return '';
		}

		if (this.order.previsionItem && this.order.previsionItem === 'X') {
			return this.translationService.getTR('global.yes');
		}
		return this.translationService.getTR('global.no');
	}

	getProvisionalOrFirmItem() {
		if (!this.order) {
			return UNDERSCORE;
		}
		if (this.order.provisionalOrFirmItem === 'P') {
			return this.translationService.getTR('order.provisional');
		}

		if (this.order.provisionalOrFirmItem === 'F') {
			return this.translationService.getTR('order.firm');
		}
		return UNDERSCORE;
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}

		if (this.saveFavoriteOrdersSubscription) {
			this.saveFavoriteOrdersSubscription.unsubscribe();
		}

		if (this.deleteFavoriteOrdersSubscription) {
			this.deleteFavoriteOrdersSubscription.unsubscribe();
		}

		if (this.dialogSubscription) {
			this.dialogSubscription.unsubscribe();
		}

		if (this.orderDataSubscription) {
			this.orderDataSubscription.unsubscribe();
		}

		if (this.routeParamsSubscription) {
			this.routeParamsSubscription.unsubscribe();
		}

		if (this.ecallsByBatchSubscription) {
			this.ecallsByBatchSubscription.unsubscribe();
		}
		if (this.ecallSub) {
			this.ecallSub.unsubscribe();
		}
	}

	openElinkDialog() {
		this.dialog.open(ElinkComponent, {
			data: this.elinkData,
			width: '99vw',
			maxHeight: '99vh',
			maxWidth: '650px'
		});
	}

	openCommentDialog() {
		const dialogRef = this.dialog.open(DialogPinComponent, {
			data: this.order,
			width: '99vw',
			maxHeight: '99vh',
			maxWidth: '650px'
		});
		this.dialogSubscription = dialogRef.afterClosed().subscribe((result: DialogResponse) => {
			if (result.action === 'submit') {
				const existingIndex = this.exceptionReportToDisplay
					.findIndex(ex => ex.yearWeek === result.data.yearWeek);
				if (!result.isDeletion) {
					// Adding or updating an exception report
					const exceptionReport: ExceptionReportToDisplay = {
						commentFromCustomer: result.data.comment,
						commentExistForCurrentWeek: true,
						docNo: result.data.docNo,
						yearWeek: result.data.yearWeek
					};

					if (existingIndex !== -1) {
						this.exceptionReportToDisplay[existingIndex].commentFromCustomer = result.data.comment;
					} else {
						this.exceptionReportToDisplay.unshift(exceptionReport);
					}
					this.isEdit = true;
					if (this.order) {
						this.order.exceptionReport = exceptionReport;
					}
				} else {
					// Deleting an exception report
					if (existingIndex !== -1) {
						this.exceptionReportToDisplay.splice(existingIndex, 1);
					}
					this.isEdit = false;
					if (this.order) {
						this.order.exceptionReport = undefined;
					}
				}
			}
		});
	}

	getButtonName() {
		return this.isEdit ? this.translationService.getTR('exception-report.edit-request')
			: this.translationService.getTR('exception-report.make-new-request');
	}

	deleteFavoriteOrder() {
		if (!this.order || !this.order.isFavorite || !this.order.favorite?.id) {
			return;
		}

		this.isDisabled = true;

		this.deleteFavoriteOrdersSubscription = this.orderService.deleteFavoriteOrders(this.order.favorite.id)
			.subscribe({
				next: () => {
					if (!this.order) {
						return;
					}

					this.order.isFavorite = false;

					this.isDisabled = false;
				},
				error: (error: unknown) => {
					console.error(error);
					this.isDisabled = false;
				}
			});
	}

	saveFavoriteOrder() {
		if (!this.order) {
			return;
		}
		if (!this.order.supplierOrderItemNumber
			|| !this.order.supplierOrderNumber) {
			return;
		}

		this.isDisabled = true;

		const obj: FavoriteOrders = {
			supplierOrderReference: this.order.supplierOrderNumber,
			supplierOrderItem: this.order.supplierOrderItemNumber
		};

		this.saveFavoriteOrdersSubscription = this.orderService.saveFavoriteOrders(obj)
			.subscribe({
				next: res => {
					if (!this.order) {
						return;
					}

					this.order.favorite = {
						id: res.id,
						supplierOrderReference: res.supplierOrderReference,
						supplierOrderItem: res.supplierOrderItem
					};

					this.order.isFavorite = true;

					this.isDisabled = false;
				},
				error: (error: unknown) => {
					console.error(error);
					this.isDisabled = false;
				}
			});
	}

	getCharacteristic(code: string) {
		return this.orderCharacteristics?.find(c => c.code === code);
	}

	buildYearWeek(yearWeek: string) {
		const year = yearWeek.slice(0, yearWeek.indexOf('W'));
		const week = yearWeek.slice(yearWeek.indexOf('W') + 1);

		return `Week ${ week } ${ year }`;
	}

	async startCallOff() {
		if (!this.order?.supplierOrderNumber) {
			return;
		}
		const supplierOrderFilter: LuceneExpression = this.order.supplierOrderItemNumber
			? {
				left: {
					field: 'supplierOrderNumber',
					term: this.order.supplierOrderNumber,
					quoted: true,
				},
				operator: 'AND',
				right: {
					field: 'supplierOrderItemNumber',
					term: this.order.supplierOrderItemNumber,
					quoted: true,
				},
				parenthesized: true,
			}
			: {
				field: 'supplierOrderNumber',
				term: this.order.supplierOrderNumber,
				quoted: true,
			};
		this.ecallOffService.updateStep1CallableBatchesFilter(supplierOrderFilter);
		this.ecallOffService.updateEcallOffSearchFilter(this.order.supplierOrderNumber);
		await this.router.navigate(['/ecall-off/ecall-off-steps/stepMatPicking']);
	}

	getStockAlertIcon(stockAlert?: string): string {
		if (!stockAlert) {
			return '';
		}
		return this.batchservice.getStockAlertIcon(stockAlert);
	}

	getStockAlertTranslation(stockAlert?: string): string {
		if (!stockAlert) {
			return '';
		}
		return this.batchservice.getStockAlertTranslation(stockAlert);
	}

	async navigation(defaultView: UserDatacenterView, routePath: string) {
		await this.router.navigate([routePath], {
			relativeTo: this.route,
			state: { ...this.previousState, view: defaultView }
		});
	}

	async close(defaultView: UserDatacenterView) {
		let route = '';
		if (defaultView.type === 'batch') {
			route = '/order-tracking/batch/datacenter';
		}
		if (defaultView.type === 'document') {
			route = '/commercial-documents/datacenter';
		}
		await this.navigation(defaultView, route);
	}

	async seeAllDocuments() {
		const defaultView = this.defaultConfiguration.getDefaultView('document');
		const filtersList: DatacenterFilterType[] = [];
		const supplierOrderNumberValues = [];
		supplierOrderNumberValues.push(
			{
				reference: padSupplierOrderReference(this.order?.supplierOrderNumber ?? ''),
				item: this.order?.supplierOrderItemNumber,
				entityReferenceName: 'documentLines'
			}
		);
		const orderNumberFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: supplierOrderNumberValues
		};
		filtersList.push(orderNumberFilter);
		defaultView.filterState = filtersList;
		await this.close(defaultView);
	}

	async seeAllBatches() {
		const defaultView = this.defaultConfiguration.getDefaultView('batch');
		const filtersList: DatacenterFilterType[] = [];
		const statusGroupFilter: DatacenterFilterType = {
			id: 'statusGroup',
			values: [
				{
					code: '5',
					translation: 'Delivered',
				}
			]
		};
		const orderNumberFilter: DatacenterFilterType = {
			id: 'supplierOrderNumber',
			values: [
				{
					reference: this.order?.supplierOrderNumber,
					item: this.order?.supplierOrderItemNumber,
				}
			]
		};
		filtersList.push(statusGroupFilter, orderNumberFilter);
		defaultView.filterState = filtersList;
		await this.close(defaultView);
	}

	private assignFavoriteOrder(order: Partial<Order>, favoriteOrders: FavoriteOrders[]) {
		const favoriteOrderForCurrentOrder = favoriteOrders.find(favoriteOrder =>
			favoriteOrder.supplierOrderItem === order.supplierOrderItemNumber
			&& favoriteOrder.supplierOrderReference === order.supplierOrderNumber);

		if (favoriteOrderForCurrentOrder) {
			order.isFavorite = true;
			order.favorite = favoriteOrderForCurrentOrder;
		}

		return order;
	}

	private getStatusIcon() {
		if (!this.order) {
			return '';
		}

		if (!this.order.grossQtyInProduction && !this.order.grossQtyAvailableReadyToShip
			&& !this.order.grossQtyDelivered) {
			return '';
		}
		if (this.order.grossQtyInProduction) {
			if (!this.order.grossQtyAvailableReadyToShip && !this.order.grossQtyDelivered) {
				return OrderStatus.WIP;
			}
			if (this.order.grossQtyAvailableReadyToShip && !this.order.grossQtyDelivered) {
				return OrderStatus.WIP_RTS;
			}
			if (!this.order.grossQtyAvailableReadyToShip && this.order.grossQtyDelivered) {
				return OrderStatus.WIP_DELIVERED;
			}
			return OrderStatus.FULL;
		}

		if (this.order.grossQtyAvailableReadyToShip && !this.order.grossQtyDelivered) {
			return OrderStatus.RTS;
		}
		if (this.order.grossQtyAvailableReadyToShip && this.order.grossQtyDelivered) {
			return OrderStatus.RTS_DELIVERED;
		}
		return OrderStatus.DELIVERED;
	}

	private constructDocumentFilter() {
		const supplierOrderReferenceItemField: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReferenceItem`,
			term: this.supplierReferenceItem,
			quoted: true
		};
		const supplierOrderReferenceFieldFromDoc: FieldExpression = {
			field: 'supplierOrderReference',
			term: padSupplierOrderReference(this.supplierReference),
			quoted: true
		};
		const supplierOrderReferenceFieldFromDocLine: FieldExpression = {
			field: `${ DOCUMENTS_SUB_ENTITY }.supplierOrderReference`,
			term: padSupplierOrderReference(this.supplierReference),
			quoted: true
		};
		const supplierOrderReferenceField: NodeExpression = {
			left: supplierOrderReferenceFieldFromDoc,
			operator: 'OR',
			right: supplierOrderReferenceFieldFromDocLine,
			parenthesized: true
		};
		const supplierOrderNumberNode: NodeExpression = {
			left: supplierOrderReferenceField,
			operator: 'AND',
			right: supplierOrderReferenceItemField,
			parenthesized: true
		};
		return luceneToString(supplierOrderNumberNode);
	}

	private getCallableGrossQuantity() {
		const orderBy: OrderBy<keyof Batch>[] = [new OrderBy('cdd', SORT_WAY)];
		this.ecallSub = this.ecallOffService.getCallableBatches(
			['qtyBatchGross'],
			this.constructCallableBatchesFilter(),
			1,
			10_000,
			orderBy,
			// this.millCode ? [this.millCode] : [] --> this should be adjusted/updated when working on WOR-5932
		).subscribe({
			next: batchData => {
				for (const callableBatch of batchData.results) {
					if (callableBatch.qtyBatchGross) {
						this.callableGrossWeight += callableBatch.qtyBatchGross;
					}
				}
			},
			error: (err: unknown) => {
				console.error('error requesting batches', err);
			}
		});
	}

	/**
	 * Load all order batches from v5 in one call and distribute client-side.
	 */
	private loadAndDistributeBatches() {
		const fields: readonly FieldOfBatch[] = [
			'id',
			'customerOrderNumber',
			'customerOrderItemNumber',
			'supplierOrderNumber',
			'supplierOrderItemNumber',
			'batchReference',
			'supplierBatchReference',
			'qtyBatchNet',
			'qtyBatchGross',
			'thickness',
			'width',
			'length',
			'heatNumber',
			'mill',
			'packageSerialNumber',
			'millOrderNumber',
			'millOrderItemNumber',
			'statusGroup',
			'status',
			'statusDescription',
			'daysInStock',
			'readyToShipDate',
			'availableInStockDate',
			'plannedShipmentDate',
			'currentLocation',
			'currentLocationDescription',
			'supplierShipmentReference',
			'supplierShipmentReferenceItem',
			'lastDispatchAdviceNumber',
			'lastDispatchAdviceDate',
			'destination',
			'destinationDescription',
			'transportReference',
			'transportMode',
			'transportModeDescription',
			'callable'
		];
		const filter = createOrderBatchesFilter(this.supplierReference, this.supplierReferenceItem);

		this.batchesSubscription = this.batchservice
			.getBatches(
				fields,
				filter,
				0,
				LIMIT,
				new OrderBy('batchReference', OrderByDirection.ASC),
				false
			)
			.subscribe(data => {
				const preparedBatches = prepareOrderCardBatches(data.results, this.user.hasService('PCALL'));
				this.callableBatchesCount = preparedBatches.callableBatchesCount;
				this.distributeReadyToShipAndCalledBatches(
					preparedBatches.readyToShipBatches,
					preparedBatches.calledBatches
				);
				this.distributeBatchesToGrids(preparedBatches.groupedOrderBatches, preparedBatches.allOrderBatches);
				this.updateAvailableTotals();
				this.iconName = this.getStatusIcon();
			});
	}

	/**
	 * Client-side distribution for all non-delivered batch groups.
	 * Delivered stays on its own backend call to preserve legacy behavior.
	 * @param groupedBatches - Batches already grouped by status group
	 * @param allBatches - Complete list of non-ready-to-ship batches returned from API
	 */
	private distributeBatchesToGrids(groupedBatches: GroupedOrderBatches, allBatches: OrderBatch[]) {
		this.orderBatches = allBatches;
		// Further processing within each group
		this.distributeBlockedBatches(groupedBatches.blockedBatches);
		this.distributeLandTransitBatches(groupedBatches.transitBatches);
		this.distributeAtReshipperBatches(groupedBatches.transitBatches);
		this.distributeOnVesselBatches(groupedBatches.transitBatches);
		this.distributeOverseasTransitBatches(groupedBatches.transitBatches);
		this.distributeTransitToCustomerBatches(groupedBatches.transitBatches);
		this.distributeConsignmentStockBatches(groupedBatches.consignmentBatches);
	}

	/** Recompute available totals from all sub-section sums. */
	private updateAvailableTotals() {
		this.sumGrossAvailableBatches =
			this.sumGrossReadyToShipBatches
			+ this.sumGrossCalledBatches
			+ this.sumGrossLandTransitBatches
			+ this.sumGrossAtReshipperBatches
			+ this.sumGrossOnVesselBatches
			+ this.sumGrossOverseasTransitBatches
			+ this.sumGrossTransitToCustomerBatches
			+ this.sumGrossConsignmentStockBatches;
		this.sumNetAvailableBatches =
			this.sumNetReadyToShipBatches
			+ this.sumNetCalledBatches
			+ this.sumNetLandTransitBatches
			+ this.sumNetAtReshipperBatches
			+ this.sumNetOnVesselBatches
			+ this.sumNetOverseasTransitBatches
			+ this.sumNetTransitToCustomerBatches
			+ this.sumNetConsignmentStockBatches;
	}

	private constructDeliveredGrid() {
		const statusGroup = 'batchStatusGroup:5';
		this.batchesSubscription = this.orderService
			.getBatchesByOrderCard(
				this.supplierReference,
				this.supplierReferenceItem,
				statusGroup,
				DELIVERED_BATCH_DISPLAY_LIMIT
			)
			.subscribe(batchesData => {
				this.rowDataDeliveredBatches = batchesData.results;
				for (const data of this.rowDataDeliveredBatches) {
					if (this.comparePlannedShipmentDate(data.plannedShipmentDate)) {
						this.rowDataDeliveredBatches.splice(this.rowDataDeliveredBatches.indexOf(data), 1);
					}
				}
				this.gridBatchDeliveredOptions = this.batchGridBuilder.buildDeliveredGrid(this.rowDataDeliveredBatches);
			});
	}

	private constructDeliveredGridTotalQty() {
		const statusGroup = 'batchStatusGroup:5';
		const distinct = true;
		const fields = ['qtyBatchGross', 'qtyBatchNet'];
		const orderBy = 'qtyBatchGross asc';
		this.batchesSubscription = this.orderService
			.getTotalQuantity(
				this.supplierReference,
				this.supplierReferenceItem,
				statusGroup,
				distinct,
				fields,
				orderBy
			)
			.subscribe(batchesData => {
				this.sumGrossDeliveredBatches =
					batchesData.results.length > 0 ? batchesData.results[0].qtyBatchGross : 0;
				this.sumNetDeliveredBatches =
					batchesData.results.length > 0 ? batchesData.results[0].qtyBatchNet : 0;
			});
	}

	/**
	 * Distribute blocked batches (statusGroup = 1) to blocked grid
	 * @param batches - List of blocked batches to display
	 */
	private distributeBlockedBatches(batches: OrderBatch[]) {
		this.rowDataBlockedBatches = batches;
		this.sumGrossBlockedBatches = this.sumGross(this.rowDataBlockedBatches);
		this.sumNetBlockedBatches = this.sumNet(this.rowDataBlockedBatches);
		this.gridBatchBlockedOptions = this.batchGridBuilder.buildBlockedBatchesGrid(batches);
	}

	private distributeReadyToShipAndCalledBatches(readyToShipBatches: Batch[], calledBatches: Batch[]) {
		this.rowDataReadyToShipBatches = readyToShipBatches;
		this.sumGrossReadyToShipBatches = this.sumGross(readyToShipBatches);
		this.sumNetReadyToShipBatches = this.sumNet(readyToShipBatches);
		this.gridBatchReadyToShipOptions = this.batchGridBuilder.buildReadyToShipGrid(readyToShipBatches);

		this.rowDataCalledBatches = calledBatches;
		this.sumGrossCalledBatches = this.sumGross(calledBatches);
		this.sumNetCalledBatches = this.sumNet(calledBatches);
		this.gridBatchCalledOptions = this.batchGridBuilder.buildCalledBatchesGrid(calledBatches);
	}

	/**
	 * Distribute land-transit batches (statusGroup = 3)
	 * Status codes: 11, 06, 16, 41
	 * @param transitBatches - List of transit batches returned from API
	 */
	private distributeLandTransitBatches(transitBatches: OrderBatch[]) {
		const statusCodes = new Set(['11', '06', '16', '41']);
		const batches = transitBatches.filter(b => statusCodes.has(b.batchStatus));
		this.rowDataLandTransitBatches = batches;
		this.sumGrossLandTransitBatches = this.sumGross(this.rowDataLandTransitBatches);
		this.sumNetLandTransitBatches = this.sumNet(this.rowDataLandTransitBatches);
		this.gridBatchLandTransitOptions = this.batchGridBuilder.buildLandVesselGrid(this.rowDataLandTransitBatches);
	}

	/**
	 * Distribute at-reshipper batches (statusGroup = 3)
	 * Status code: 13
	 * @param transitBatches -List of transit batches returned from API
	 */
	private distributeAtReshipperBatches(transitBatches: OrderBatch[]) {
		const statusCodes = new Set(['13']);
		const batches = transitBatches.filter(b => statusCodes.has(b.batchStatus));
		this.rowDataAtReshipperBatches = batches;
		this.sumGrossAtReshipperBatches = this.sumGross(this.rowDataAtReshipperBatches);
		this.sumNetAtReshipperBatches = this.sumNet(this.rowDataAtReshipperBatches);
		this.gridBatchAtReshipperOptions = this.batchGridBuilder.buildAtReshipperGrid(this.rowDataAtReshipperBatches);
	}

	/**
	 * Distribute on-vessel batches (statusGroup = 3)
	 * Status codes: 21, 22, 25, 26, 27
	 * @param transitBatches - List of transit batches returned from API
	 */
	private distributeOnVesselBatches(transitBatches: OrderBatch[]) {
		const statusCodes = new Set(['21', '22', '25', '26', '27']);
		const batches = transitBatches.filter(b => statusCodes.has(b.batchStatus));
		this.rowDataOnVesselBatches = batches;
		this.sumGrossOnVesselBatches = this.sumGross(this.rowDataOnVesselBatches);
		this.sumNetOnVesselBatches = this.sumNet(this.rowDataOnVesselBatches);
		this.gridBatchOnVesselOptions = this.batchGridBuilder.buildLandVesselGrid(this.rowDataOnVesselBatches);
	}

	/**
	 * Distribute overseas-transit batches (statusGroup = 3)
	 * Status codes: 31, 33, 34, 36, 37
	 * @param transitBatches - List of transit batches returned from API
	 */
	private distributeOverseasTransitBatches(transitBatches: OrderBatch[]) {
		const statusCodes = new Set(['31', '33', '34', '36', '37']);
		const batches = transitBatches.filter(b => statusCodes.has(b.batchStatus));
		this.rowDataOverseasTransitBatches = batches;
		this.sumGrossOverseasTransitBatches = this.sumGross(this.rowDataOverseasTransitBatches);
		this.sumNetOverseasTransitBatches = this.sumNet(this.rowDataOverseasTransitBatches);
		this.gridBatchOverseasTransitOptions =
			this.batchGridBuilder.buildConsignmentOverseasGrid(this.rowDataOverseasTransitBatches);
	}

	/**
	 * Distribute transit-to-customer batches (statusGroup = 3)
	 * Status codes: 12, 15, 42, 43
	 * @param transitBatches - List of transit batches returned from API
	 */
	private distributeTransitToCustomerBatches(transitBatches: OrderBatch[]) {
		const statusCodes = new Set(['12', '15', '42', '43']);
		const batches = transitBatches.filter(b => statusCodes.has(b.batchStatus));
		this.rowDataTransitToCustomerBatches = batches;
		this.sumGrossTransitToCustomerBatches = this.sumGross(batches);
		this.sumNetTransitToCustomerBatches = this.sumNet(batches);
		this.gridBatchTransitToCustomerOptions = this.batchGridBuilder.buildLandVesselGrid(batches);
	}

	/**
	 * Distribute consignment-stock batches (statusGroup = 4)
	 * Status codes: 07, 38
	 * @param batches - List of consignment-stock batches returned from API
	 */
	private distributeConsignmentStockBatches(batches: OrderBatch[]) {
		const statusCodes = new Set(['07', '38']);
		const filteredBatches = batches.filter(b => statusCodes.has(b.batchStatus));
		this.rowDataConsignmentStockBatches = filteredBatches;
		this.sumGrossConsignmentStockBatches = this.sumGross(this.rowDataConsignmentStockBatches);
		this.sumNetConsignmentStockBatches = this.sumNet(this.rowDataConsignmentStockBatches);
		this.gridBatchConsignmentStockOptions =
			this.batchGridBuilder.buildConsignmentOverseasGrid(this.rowDataConsignmentStockBatches);
	}

	private comparePlannedShipmentDate(date: string) {
		const plannedShipmentDate = this.dateFormat.transform(date, 'date');
		const sixMonths = this.dateFormat.transform(moment().subtract(6, 'months'), 'date');
		return moment(plannedShipmentDate).isBefore(sixMonths);
	}

	private constructInProductionGrid() {
		if (!this.wipOrders) {
			return;
		}
		this.sumInProduction = this.sumProduction(this.wipOrders);
		this.gridInProductionOptions = new CommonGridOptions<WipOrder>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: this.wipOrders,
			domLayout: 'autoHeight',
			columnDefs: [
				new CommonColDef<WipOrder>({
					headerName: this.translationService.getTR('order-card.work-in-progress'),
					fields: ['statusWorkInProgress', 'statusWorkInProgressDescription'],
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const wipOrder = params.data;
						return wipOrder.statusWorkInProgressDescription || wipOrder.statusWorkInProgress;
					},
				}),
				new NumericColDef<WipOrder>({
					headerName: this.translationService.getTR('order-card.wip-quantity'),
					field: 'qtyWorkInProgress',
					cellRendererParams: { numericFormat: 'weight-with-zero' }
				}),
				new DateColDef<WipOrder>({
					headerName: this.translationService.getTR('order.wip.forecast.stock'),
					field: 'forecastInStock',
					minWidth: 84,
					maxWidth: 84,
					width: 84,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'week' }
				})
			]
		});
	}

	private constructCommercialDocuments() {
		if (this.documentResult) {
			for (const document of this.documentResult.results) {
				const globalDocument: GlobalDocument = {
					title: document.type === 'IN' ? this.translationService.getTR('order.card.document.invoices')
						: document.type === 'DA' ? this.translationService.getTR('order.card.document.dispatch.advices')
							: document.type === 'CE' ? this.translationService.getTR('order.card.document.certificates')
								: document.type === 'XCARB'
									? this.translationService.getTR('order.card.xcarb')
									: this.translationService.getTR('order.card.document.order.acknowledgments'),
					name: document.name,
					date: document.date,
					id: document.id,
					subtype: document.subtypeDescription,
					type: document.type,
					proforma: document.proforma
				};

				this.globalDocuments.push(globalDocument);
			}
		}
		this.constructGridCommercialDocumentsOptions(this.globalDocuments);
	}

	private constructGridCommercialDocumentsOptions(globalDocuments: GlobalDocument[]) {
		this.gridCommercialDocumentsOptions = new CommonGridOptions<GlobalDocument>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			rowData: globalDocuments,
			headerHeight: 60,

			columnDefs: [
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.document'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'title',
				}),
				new CommonColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.type'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'subtype',
				}),
				...globalDocuments.some(d => d.proforma === 'true') ? [
					new CommonColDef<GlobalDocument>({
						headerName: this.translationService.getTR('order-card.proforma'),
						headerComponent: CheckboxHeaderComponent,
						field: 'proforma',
						sortable: true,
						valueFormatter: params => {
							if (params.value === 'true') {
								return this.translationService.getTR('global.yes');
							}
							if (params.value === 'false') {
								return this.translationService.getTR('global.no');
							}
							return '';
						}
					})
				] : [],
				new LinkColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order-card.name'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					field: 'name',
					cellRendererParams: linkToDocumentCellRendererParams
				}),
				new DateColDef<GlobalDocument>({
					headerName: this.translationService.getTR('order.creation.date'),
					headerComponent: CheckboxHeaderComponent,
					sortable: true,
					sort: 'desc',
					field: 'date',
					width: 100,
					cellRendererParams: { inputFormat: 'isoDate', outputFormat: 'date' }
				}),
				new IconRedirectColDef<GlobalDocument>({
					field: 'name',
					cellRendererParams: {
						linkData: (params: ICellRendererParams<GlobalDocument, unknown>) => {
							const { data } = params;
							if (!data) {
								return undefined;
							}
							return {
								url: this.commercialDocumentService
									.getDownloadDocumentUrl(data.id, data.name),
								type: 'application/pdf',
								icon: 'table_document'
							};
						}
					}
				})
			]
		});
	}

	private constructOrderStatusGrid() {
		if (!this.order) {
			return;
		}
		this.orderStatusData = this.createOrderStatusDataArray();
		this.gridOrderStatusOptions = new CommonGridOptions<OrderStatusData>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			rowData: this.orderStatusData,
			domLayout: 'autoHeight',
			headerHeight: 85,
			columnDefs: [
				new CommonColDef<OrderStatusData>({
					field: 'title',
					sortable: false
				}),
				new NumericPercentageColDef<OrderStatusData>({
					headerName: this.translationService.getTR('order.ordered-t'),
					headerComponentParams: {
						iconName: 'ordered',
						value: this.totalOrdered,
					},
					valueTypeGetter(params) {
						const { data } = params;
						return data ? { type: data.type } : undefined;
					},
					headerClass: 'copo-center',
					sortable: false
				}),
				new NumericPercentageColDef<OrderStatusData>({
					headerName: this.translationService.getTR('order-tracking.in.production-t'),
					headerComponentParams: {
						iconName: 'in_production',
						value: this.totalInProduction
					},
					valueTypeGetter(params) {
						const { data } = params;
						return data ? { value: data.production, type: data.type } : undefined;
					},
					headerClass: 'copo-center',
					sortable: false
				}),
				new NumericPercentageColDef<OrderStatusData>({
					headerName: this.translationService.getTR('order-tracking.in.stock-t'),
					headerComponentParams: {
						iconName: 'in_stock',
					},
					valueTypeGetter(params) {
						const { data } = params;
						return data ? { value: data.stock, type: data.type } : undefined;
					},
					headerClass: 'copo-center',
					sortable: false
				}),
				new NumericPercentageColDef<OrderStatusData>({
					headerName: this.translationService.getTR('order-tracking.in.transit-t'),
					headerComponentParams: {
						iconName: 'in_transit',
					},
					valueTypeGetter(params) {
						const { data } = params;
						return data ? { value: data.transit, type: data.type } : undefined;
					},
					headerClass: 'copo-center',
					sortable: false
				}),
				new NumericPercentageColDef<OrderStatusData>({
					headerName: this.translationService.getTR('order.card.in-consignment-t'),
					headerComponentParams: {
						iconName: 'in_stock',
					},
					valueTypeGetter(params) {
						const { data } = params;
						return data ? { value: data.consignment, type: data.type } : undefined;
					},
					hide: this.orderStatusData.length < 2
						|| (!this.orderStatusData[0].consignment && !this.orderStatusData[1].consignment),
					headerClass: 'copo-center',
					sortable: false
				}),
				new NumericPercentageColDef<OrderStatusData>({
					headerName: this.translationService.getTR('order-tracking.delivered-t'),
					headerComponentParams: {
						iconName: 'delivered',
					},
					valueTypeGetter(params) {
						const { data } = params;
						return data ? { value: data.delivered, type: data.type } : undefined;
					},
					headerClass: 'copo-center',
					sortable: false
				})
			]
		});
	}

	private createOrderStatusDataArray() {
		const statusArray: OrderStatusData[] = [];
		if (!this.order?.grossQtyOrdered
			|| !this.order.grossQtyInProduction
			|| !this.order.grossQtyAvailableReadyToShip
			|| !this.order.grossQtyAvailableInTransit
			|| !this.order.grossQtyAvailableInConsignment
			|| !this.order.grossQtyDelivered
			|| !this.order.netQtyAvailableReadyToShip
			|| !this.order.netQtyAvailableInTransit
			|| !this.order.netQtyAvailableInConsignment
			|| !this.order.netQtyDelivered
			|| !this.order.grossInProductionPercentage
			|| !this.order.grossAvailableReadyToShipPercentage
			|| !this.order.grossAvailableInTransitPercentage
			|| !this.order.grossAvailableInConsignmentPercentage
			|| !this.order.grossDeliveredPercentage) {
			return statusArray;
		}

		this.totalOrdered = this.order.grossQtyOrdered;
		this.totalInProduction = this.order.grossQtyInProduction;

		statusArray.push({
			title: this.translationService.getTR('settings.weight.gross'),
			stock: this.order.grossQtyAvailableReadyToShip,
			transit: this.order.grossQtyAvailableInTransit,
			consignment: this.order.grossQtyAvailableInConsignment,
			delivered: this.order.grossQtyDelivered,
			type: 'weight-with-zero'
		}, {
			title: this.translationService.getTR('settings.weight.net'),
			stock: this.order.netQtyAvailableReadyToShip,
			transit: this.order.netQtyAvailableInTransit,
			consignment: this.order.netQtyAvailableInConsignment,
			delivered: this.order.netQtyDelivered,
			type: 'weight-with-zero'
		}, {
			title: `% (${ this.translationService.getTR('settings.weight.gross') })`,
			production: this.order.grossInProductionPercentage,
			stock: this.order.grossAvailableReadyToShipPercentage,
			transit: this.order.grossAvailableInTransitPercentage,
			consignment: this.order.grossAvailableInConsignmentPercentage,
			delivered: this.order.grossDeliveredPercentage,
			type: 'percentage'
		});
		return statusArray;
	}

	private getOrderData() {
		return this.getObservableForCard();
	}

	private getObservableForCard() {
		const orderFields: FieldOfOrder[] = [...orderCardFields];
		if (this.hasExceptionReportService) {
			orderFields.push('comments', 'answers');
		}
		return forkJoin([
			this.orderService.getOrderCard(
				this.supplierReference,
				this.supplierReferenceItem,
				orderFields
			),
			this.orderService.getWipByOrderCard(
				this.supplierReference,
				this.supplierReferenceItem
			),
			this.invoiceDisplayed || this.dispatchAdviceDisplayed
			|| this.orderAcknowledgmentDisplayed || this.certificateDisplayed
			|| this.isXcarbDisplayed
				? this.commercialDocumentService.getDocuments(
					this.documentsFields,
					this.constructDocumentFilter(),
					0,
					10,
					new OrderBy(SORT_BY, SORT_WAY),
					false
				).pipe(
					catchError((err: unknown) => {
						console.error(err);
						this.hasCommercialDocumentError = true;
						return of(undefined);
					})
				)
				: of(undefined),
			this.orderService.getFavoriteOrders(),
			this.orderService.getOrderCharacteritics(
				this.supplierReference,
				this.supplierReferenceItem
			),
		]);
	}

	private constructElinkData() {
		if (!this.order?.customerOrderNumber
			|| !this.order.customerOrderItemNumber
			|| !this.order.supplierOrderNumber
			|| !this.order.supplierOrderItemNumber
			|| !this.order.millOrderNumber
			|| !this.order.millOrderItemNumber
			|| !this.order.millDescription
			|| !this.order.mill
			|| !this.order.productDescription
			|| !this.order.thickness
			|| !this.order.width
			|| !this.order.cddWeek
			|| !this.order.soldTo
			|| !this.order.grossQtyOrdered
		) {
			return;
		}
		const quantityBySettings = this.order.grossQtyOrdered;
		this.millCode = this.order.mill;
		this.elinkData = {
			customerOrderNumber: this.order.customerOrderNumber,
			customerOrderItemNumber: this.order.customerOrderItemNumber,
			supplierOrderNumber: this.order.supplierOrderNumber,
			supplierOrderItemNumber: this.order.supplierOrderItemNumber,
			millOrderNumber: this.order.millOrderNumber,
			millOrderItemNumber: this.order.millOrderItemNumber,
			mill: this.order.millDescription,
			millCode: this.order.mill,
			productCategory: this.order.productDescription,
			thickness: this.order.thickness,
			width: this.order.width,
			orderedQuantity: quantityBySettings,
			cdd: this.order.cddWeek,
			soldTo: this.order.soldTo
		};

		this.elinkSub = this.orderService.getMillMail(this.millCode).subscribe({
			next: (millEmail: string) => {
				this.millEmail = millEmail;
			}
		});
	}

	private constructCallableBatchesFilter() {
		const supplierOrderReferenceField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: this.supplierReference,
			quoted: false,
		};

		const supplierOrderItemReferenceField: FieldExpression = {
			field: 'supplierOrderItemNumber',
			term: this.supplierReferenceItem,
			quoted: false,
		};

		return {
			left: supplierOrderReferenceField,
			operator: 'AND',
			right: supplierOrderItemReferenceField,
			parenthesized: true,
		};
	}

	private getEcallsByBatch() {
		this.ecallsByBatchSubscription = this.ecallOffService
			.getEcallsForOrderCard(this.supplierReference, this.supplierReferenceItem).subscribe({
				next: (result: EcallOffCardResponse[]) => {
					this.ecallData = result;
					this.updateEcallDataWeightAndType(this.ecallData);
					this.gridEcallofBatchesFields();
				},
				error: (error: unknown) => {
					console.error('Error when getting ecalls', error);
				}
			});
	}

	private updateEcallDataWeightAndType(ecallData: EcallOffCardResponse[]) {
		for (const ecall of ecallData) {
			for (const item of ecall.items) {
				(<EcallOffCardItemResponse & { requestedDateForCallOffType?: string }>item)
					.requestedDateForCallOffType = ecall.requestedDateForCallOffType;
			}
			ecall.quantitySum = 0;
			if (ecall.items.length > 0) {
				ecall.quantitySum = this.getTotalGrossWeightBatches(ecall.items);
			}
		}
	}

	private getTotalGrossWeightBatches(items: EcallOffCardItemResponse[]) {
		return items.reduce((sum, item) => sum + Number(item.qtyBatchBrut || 0), 0);
	}

	private gridEcallofBatchesFields() {
		this.gridEcallCalledBatches = this.batchGridBuilder
			.buildEcallBatchesGrid(this.order?.supplierOrderNumber, this.order?.supplierOrderItemNumber);
	}
}
